import { FeedingPlanEditor } from '@/components/FeedingPlanEditor';
import {
  EMPTY_FEEDING_DRAFT,
  type FeedingPlanDraft,
  draftHasContent,
  draftToBody,
  validateFeedingDraft,
} from '@/lib/feeding';
import { Check, CircleAlert, Mail, Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { BrandedShell } from '../components/BrandedShell';
import { MagicLinkForm } from '../components/MagicLinkForm';
import { PayBookingPanel } from '../components/PayBookingPanel';
import { CustomerApiError, customerApi } from '../customer_api';
import {
  type GuestPet,
  type GuestSizeCategory,
  type GuestSpecies,
  clearBookDraft,
  guestPetsToDescriptors,
  guestPetsToParam,
  isValidDraftId,
  loadBookDraft,
  newDraftId,
  saveBookDraft,
} from '../lib/book_draft';
import { formatDate, formatMoney } from '../lib/format';
import { buildReturnTo } from '../lib/return_to';
import { getStripePublishableKey } from '../lib/stripe_env';
import { useSpaceNoun } from '../lib/useSpaceNoun';
import { useCustomerAuth } from '../useCustomerAuth';
import { RecurringDaycarePanel } from './book/RecurringDaycarePanel';

/**
 * Sub-spec 01b — /book wizard.
 *
 * Single branching wizard hosting boarding / daycare / grooming flows. URL is
 * the source of truth for step + selections so the wizard is resumable on
 * reload and deep-linkable from marketing emails. Larger state (intake
 * answers, quote snapshot) is held in component state for the lifetime of
 * the wizard session and mirrored into a localStorage draft (see
 * `lib/book_draft.ts`) so a magic-link round trip restores it.
 *
 * Per spec §2 D1, this is ONE component rather than three. Step 1 picks the
 * service category; subsequent steps swap based on category via the
 * `STEPS_FOR` registry (D12).
 *
 * Guest flow (no session): the visitor describes pets inline, sees real
 * availability + a real quote, and is only asked to sign in at the `account`
 * step — the one that creates the booking. After the magic link brings them
 * back (`/book?draft=<id>&step=account…`) the wizard restores the draft,
 * offers to match the inline pets to pets already on file (or creates
 * them), and continues straight to payment.
 *
 * Server contract: see `apps/api/src/routes/customer_bookings_create.ts` and
 * `apps/api/src/routes/customer_guest_booking.ts`.
 */

type ServiceCategory = 'boarding' | 'daycare' | 'grooming';

interface ServiceLite {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  category: string;
  iconSlug: string | null;
  applicableTo: Record<string, boolean>;
  requiresAppointment: boolean;
  defaultDurationMin: number | null;
  defaultPriceMinor: number;
  currency: string;
  paymentPolicy: 'pay_now' | 'pay_later' | 'deposit_then_balance' | 'no_payment';
  paymentTiming: string;
  /** Migration 0095 — service is in request-and-approve mode. */
  requiresApproval?: boolean;
}

interface PetLite {
  id: string;
  name: string;
  species: string;
  sizeCategory: string | null;
  /** Migration 0099 — evaluation / temperament day gate. */
  assessmentStatus?: string;
  assessmentExpiresAt?: string | null;
}

/** Public settings for the evaluation gate (`GET /api/customer/assessments/settings`). */
interface AssessmentGateSettings {
  daycareRequiresAssessment: boolean;
  boardingRequiresAssessment: boolean;
  service: { id: string; name: string } | null;
}

/**
 * Why a pet cannot be booked into `category` under the tenant's evaluation
 * gate, or null when it can. Mirrors `findAssessmentBlocks` on the server.
 */
function assessmentBlockReason(
  pet: PetLite,
  category: ServiceCategory | null,
  gate: AssessmentGateSettings | null,
): string | null {
  if (!gate || !category) return null;
  const required =
    (category === 'daycare' && gate.daycareRequiresAssessment) ||
    (category === 'boarding' && gate.boardingRequiresAssessment);
  if (!required) return null;
  const status = pet.assessmentStatus ?? 'not_required';
  const current =
    status === 'passed' &&
    (!pet.assessmentExpiresAt || pet.assessmentExpiresAt > new Date().toISOString());
  if (current) return null;
  if (status === 'scheduled') {
    return `${pet.name}'s evaluation day is booked but hasn't happened yet.`;
  }
  if (status === 'failed') {
    return `${pet.name} didn't pass their last evaluation — please get in touch before booking.`;
  }
  if (status === 'passed' || status === 'expired') {
    return `${pet.name}'s evaluation has expired — book a new evaluation day first.`;
  }
  return `${pet.name} needs an evaluation day first — book one.`;
}

interface RunLite {
  id: string;
  name: string;
  facilityId: string;
  sizeCategory: string;
  speciesAllowed: string[];
}

interface GroomerLite {
  id: string;
  displayName: string;
}

interface Slot {
  startAt: string;
  durationMin: number;
}

interface IntakeQuestion {
  slug: string;
  label: string;
  kind: 'boolean' | 'acknowledgement' | 'select';
  required: boolean;
  isAcknowledgement: boolean;
  options: string[] | null;
}

interface QuoteSnapshot {
  totalMinor: number;
  currency: string;
  quotes: Array<{
    petId: string;
    totalMinor: number;
    quote: {
      base_minor: number;
      modifiers: Array<{
        label: string;
        kind: 'discount' | 'surcharge' | 'credit_burn';
        amount_minor: number;
      }>;
      total_minor: number;
      deposit_minor: number;
      balance_minor: number;
      payment_timing: 'full' | 'deposit';
    };
  }>;
}

interface VaccineRequiredError {
  petId: string;
  petName?: string;
  vaccineId: string;
}

interface CreateBookingResponse {
  primaryBookingId: string;
  bookingGroupId?: string;
  bookings: Array<{
    id: string;
    type: string;
    status: string;
    paymentPolicy: ServiceLite['paymentPolicy'];
    totalMinor: number;
  }>;
}

type IntakeAnswers = Record<string, Record<string, { answer: 0 | 1; note?: string | undefined }>>;

const STEP_LABELS: Record<string, string> = {
  service: 'Choose a service',
  pets: 'Your pets',
  dates: 'Choose dates',
  run: 'Choose a run',
  daycare_dates: 'Choose date(s)',
  session: 'Session type',
  grooming_date: 'Choose date',
  groomer: 'Choose a groomer',
  slot: 'Choose a time slot',
  intake: 'A few questions',
  review: 'Review your quote',
  account: 'Your account',
  payment: 'Payment',
};

/**
 * Step label with the tenant's "space" noun applied to the run-picker step
 * ("Choose a pen" for a cattery). Rendered inside `BrandedShell` so the
 * noun context is available.
 */
function StepLabel({ step }: { step: string }) {
  const noun = useSpaceNoun();
  if (step === 'run') return <>Choose a {noun.singular}</>;
  return <>{STEP_LABELS[step]}</>;
}

/**
 * Step registry per vertical. `includeAccount` inserts the `account` step
 * (sign-in and/or pet matching) before payment — present whenever the
 * visitor has no session or is booking for pets described inline.
 */
function STEPS_FOR(category: ServiceCategory | null, includeAccount: boolean): readonly string[] {
  let steps: string[];
  if (category === 'boarding') {
    steps = ['service', 'pets', 'dates', 'run', 'intake', 'review', 'payment'];
  } else if (category === 'daycare') {
    steps = ['service', 'pets', 'daycare_dates', 'session', 'intake', 'review', 'payment'];
  } else if (category === 'grooming') {
    steps = ['service', 'pets', 'grooming_date', 'groomer', 'slot', 'intake', 'review', 'payment'];
  } else {
    return ['service'];
  }
  if (includeAccount) steps.splice(steps.length - 1, 0, 'account');
  return steps;
}

const GUEST_PETS_MARKER = 'guest';

function tenantKey(): string {
  try {
    return window.location.host || 'default';
  } catch {
    return 'default';
  }
}

// ---------------------------------------------------------------------------
// Top-level wizard
// ---------------------------------------------------------------------------

export function BookWizard() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useCustomerAuth();

  const stepParam = params.get('step') ?? 'service';
  const categoryParam = (params.get('type') as ServiceCategory | null) ?? null;
  const serviceId = params.get('serviceId') ?? '';
  const petsParam = params.get('pets') ?? '';
  const petMode: 'account' | 'guest' = petsParam === GUEST_PETS_MARKER ? 'guest' : 'account';
  const draftId = params.get('draft');

  // Catalogue/data
  const [services, setServices] = useState<ServiceLite[] | null>(null);
  const [pets, setPets] = useState<PetLite[] | null>(null);
  // Migration 0099 — evaluation gate settings (public; null until loaded).
  const [assessmentGate, setAssessmentGate] = useState<AssessmentGateSettings | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Resolved once on mount — single-facility tenants get it transparently.
  const [resolvedFacilityId, setResolvedFacilityId] = useState<string>('');

  // Guest pets — described inline by a visitor with no pets on file.
  const [guestPets, setGuestPets] = useState<GuestPet[]>([]);

  // Boarding state
  const [checkIn, setCheckIn] = useState<string>(params.get('checkIn') ?? '');
  const [checkout, setCheckOut] = useState<string>(params.get('checkout') ?? '');
  const [runIdByPet, setRunIdByPet] = useState<Record<string, string | null>>({});

  // Daycare state
  const [daycareDates, setDaycareDates] = useState<string[]>(
    (params.get('daycareDates') ?? '').split(',').filter(Boolean),
  );
  const [sessionType, setSessionType] = useState<'full_day' | 'half_day_am' | 'half_day_pm'>(
    (params.get('sessionType') as 'full_day' | 'half_day_am' | 'half_day_pm') ?? 'full_day',
  );

  // Grooming state
  const [groomingDate, setGroomingDate] = useState<string>(params.get('groomingDate') ?? '');
  const [groomerId, setGroomerId] = useState<string | null>(params.get('groomerId'));
  const [groomingStart, setGroomingStart] = useState<string>(params.get('startTime') ?? '');

  // Intake answers: petId -> slug -> { answer, note }
  const [intakeAnswers, setIntakeAnswers] = useState<IntakeAnswers>({});
  // Migration 0101 — structured feeding plan per pet, collected on the intake
  // step for boarding + daycare and PUT to the pet after the booking lands.
  const [feedingPlanByPet, setFeedingPlanByPet] = useState<Record<string, FeedingPlanDraft>>({});

  // Quote + booking results
  const [quote, setQuote] = useState<QuoteSnapshot | null>(null);
  const [createdBooking, setCreatedBooking] = useState<CreateBookingResponse | null>(null);
  const [vaccineGate, setVaccineGate] = useState<VaccineRequiredError | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [discountCode, setDiscountCode] = useState<string>('');
  // Minimum-stay violation for the current boarding dates (pricing_rules
  // kind='min_stay'). Set from the preview-quote 422 on the dates step.
  const [minStayMessage, setMinStayMessage] = useState<string | null>(null);

  // Draft hydration gate — the save effect must not overwrite a stored draft
  // with empty state before the load effect has run.
  const [hydrated, setHydrated] = useState(false);
  const hydratingRef = useRef(false);

  const needsAccountStep = !user || petMode === 'guest';
  const stepsForBranch = STEPS_FOR(categoryParam, needsAccountStep);
  const stepIndex = Math.max(0, stepsForBranch.indexOf(stepParam));
  const totalSteps = stepsForBranch.length;
  const currentStep = stepsForBranch[stepIndex] ?? 'service';

  const petIds = useMemo(() => {
    if (petMode === 'guest') return guestPets.map((p) => p.key);
    return petsParam
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }, [petMode, guestPets, petsParam]);

  const selectedService = useMemo(
    () => services?.find((s) => s.id === serviceId) ?? null,
    [services, serviceId],
  );
  const selectedPets = useMemo<PetLite[]>(() => {
    if (petMode === 'guest') {
      return guestPets.map((p) => ({
        id: p.key,
        name: p.name.trim() || 'Your pet',
        species: p.species,
        sizeCategory: p.sizeCategory || null,
      }));
    }
    return (pets ?? []).filter((p) => petIds.includes(p.id));
  }, [petMode, guestPets, pets, petIds]);

  // Pet descriptors for the public availability probe — species + size only.
  const petDescriptorParam = useMemo(
    () =>
      guestPetsToParam(
        selectedPets.map((p) => ({ species: p.species, sizeCategory: p.sizeCategory })),
      ),
    [selectedPets],
  );
  const guestSpecies = useMemo(
    () => Array.from(new Set(selectedPets.map((p) => p.species))),
    [selectedPets],
  );

  // -------------------------------------------------------------------------
  // Draft restore + persist
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (hydratingRef.current) return;
    hydratingRef.current = true;
    const existing = params.get('draft');
    if (isValidDraftId(existing)) {
      const draft = loadBookDraft(tenantKey(), existing);
      if (draft) {
        setGuestPets(draft.guestPets);
        setRunIdByPet(draft.runIdByPet);
        setIntakeAnswers(draft.intakeAnswers);
        setDiscountCode(draft.discountCode);
        setFeedingPlanByPet((draft.feedingPlanByPet ?? {}) as Record<string, FeedingPlanDraft>);
        // A bare `/book?draft=…` (the magic-link return) restores the full
        // wizard URL from the draft; an explicit URL wins otherwise.
        const stored = new URLSearchParams(draft.params);
        if (!params.get('type') && stored.get('type')) {
          stored.set('draft', existing);
          setCheckIn(stored.get('checkIn') ?? '');
          setCheckOut(stored.get('checkout') ?? '');
          setDaycareDates((stored.get('daycareDates') ?? '').split(',').filter(Boolean));
          setSessionType(
            (stored.get('sessionType') as 'full_day' | 'half_day_am' | 'half_day_pm') ?? 'full_day',
          );
          setGroomingDate(stored.get('groomingDate') ?? '');
          setGroomerId(stored.get('groomerId'));
          setGroomingStart(stored.get('startTime') ?? '');
          setParams(stored, { replace: true });
        }
      }
    } else {
      const next = new URLSearchParams(params);
      next.set('draft', newDraftId());
      setParams(next, { replace: true });
    }
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated || !isValidDraftId(draftId)) return;
    const p = new URLSearchParams(params);
    p.delete('draft');
    p.delete('bookingId');
    saveBookDraft(tenantKey(), draftId, {
      params: p.toString(),
      guestPets,
      runIdByPet,
      intakeAnswers,
      discountCode,
      feedingPlanByPet,
    });
  }, [
    hydrated,
    draftId,
    params,
    guestPets,
    runIdByPet,
    intakeAnswers,
    discountCode,
    feedingPlanByPet,
  ]);

  // -------------------------------------------------------------------------
  // Initial catalogue load
  // -------------------------------------------------------------------------
  useEffect(() => {
    customerApi<{ services: ServiceLite[] }>('/api/customer/services')
      .then((r) => setServices(r.services))
      .catch((err) => {
        if (err instanceof CustomerApiError && err.status === 404) {
          setServices([]);
        } else {
          setLoadError('Could not load services');
        }
      });
    // Resolve facilityId once — used by availability + capacity checks.
    customerApi<{ facilities: Array<{ id: string }> }>('/api/customer/facilities')
      .then((r) => {
        const first = r.facilities[0];
        if (first) setResolvedFacilityId(first.id);
      })
      .catch(() => {});
  }, []);

  // Pets on file — only once we know there is a session.
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setPets([]);
      return;
    }
    customerApi<{ pets: PetLite[] }>('/api/customer/pets')
      .then((r) => setPets(r.pets))
      .catch(() => setPets([]));
  }, [authLoading, user]);

  // Evaluation gate — public, one fetch; failures just disable the blocker
  // (the server still refuses at create time).
  useEffect(() => {
    customerApi<AssessmentGateSettings>('/api/customer/assessments/settings')
      .then(setAssessmentGate)
      .catch(() => setAssessmentGate(null));
  }, []);

  // -------------------------------------------------------------------------
  // Navigation
  // -------------------------------------------------------------------------
  function goToStep(nextStep: string, extra?: Record<string, string>) {
    const next = new URLSearchParams(params);
    next.set('step', nextStep);
    if (extra) {
      for (const [k, v] of Object.entries(extra)) next.set(k, v);
    }
    setParams(next);
    setSubmitError(null);
  }

  function selectCategory(cat: ServiceCategory, sid: string) {
    const next = new URLSearchParams();
    if (draftId) next.set('draft', draftId);
    next.set('type', cat);
    next.set('serviceId', sid);
    next.set('step', 'pets');
    setParams(next);
    setQuote(null);
  }

  function nextStepAfterPets(): string {
    if (categoryParam === 'boarding') return 'dates';
    if (categoryParam === 'daycare') return 'daycare_dates';
    if (categoryParam === 'grooming') return 'grooming_date';
    return 'service';
  }

  function confirmPets(ids: string[]) {
    const next = new URLSearchParams(params);
    next.set('pets', ids.join(','));
    next.set('step', nextStepAfterPets());
    setParams(next);
    setQuote(null);
  }

  function confirmGuestPets(list: GuestPet[]) {
    setGuestPets(list);
    const next = new URLSearchParams(params);
    next.set('pets', GUEST_PETS_MARKER);
    next.set('step', nextStepAfterPets());
    setParams(next);
    setQuote(null);
  }

  function switchPetMode(mode: 'account' | 'guest') {
    const next = new URLSearchParams(params);
    if (mode === 'guest') next.set('pets', GUEST_PETS_MARKER);
    else next.delete('pets');
    setParams(next, { replace: true });
  }

  function goBack() {
    if (stepIndex === 0) {
      navigate(-1);
      return;
    }
    const prev = stepsForBranch[stepIndex - 1] ?? 'service';
    goToStep(prev);
  }

  // -------------------------------------------------------------------------
  // Quote + create-booking helpers
  // -------------------------------------------------------------------------
  interface PetOverrides {
    petIds: string[];
    runIdByPet: Record<string, string | null>;
    intakeAnswers: IntakeAnswers;
    /** Migration 0101 — guest keys re-mapped to real pet ids. */
    feedingPlanByPet?: Record<string, FeedingPlanDraft>;
  }

  /**
   * Migration 0101 — persist each pet's feeding plan once the booking exists.
   * Best-effort: a failure here must never undo a confirmed booking, so
   * errors are swallowed (the owner can still edit the plan from /account).
   */
  const saveFeedingPlans = useCallback(
    async (ids: string[], plans: Record<string, FeedingPlanDraft>) => {
      await Promise.all(
        ids.map(async (petId) => {
          const plan = plans[petId];
          if (!plan || !draftHasContent(plan) || validateFeedingDraft(plan).length > 0) return;
          try {
            await customerApi(`/api/customer/pets/${petId}/feeding-plan`, {
              method: 'PUT',
              body: JSON.stringify(draftToBody(plan)),
            });
          } catch {
            /* best-effort */
          }
        }),
      );
    },
    [],
  );

  const buildBookingRequestBody = useCallback(
    (includeIntake = false, overrides?: PetOverrides): Record<string, unknown> | null => {
      const ids = overrides?.petIds ?? petIds;
      const runs = overrides?.runIdByPet ?? runIdByPet;
      const answers = overrides?.intakeAnswers ?? intakeAnswers;
      if (!categoryParam || !serviceId || ids.length === 0) return null;
      const base: Record<string, unknown> = {
        serviceCategory: categoryParam,
        primaryServiceId: serviceId,
        petIds: ids,
      };
      if (resolvedFacilityId) base.facilityId = resolvedFacilityId;
      if (categoryParam === 'boarding') {
        if (!checkIn || !checkout) return null;
        const runMap: Record<string, string | null> = {};
        for (const id of ids) runMap[id] = runs[id] ?? null;
        base.boarding = { checkIn, checkout, runIdByPet: runMap };
      } else if (categoryParam === 'daycare') {
        if (daycareDates.length === 0) return null;
        base.daycare = { dates: daycareDates, sessionType };
      } else if (categoryParam === 'grooming') {
        if (!groomingDate || !groomingStart) return null;
        const grooming: Record<string, unknown> = {
          date: groomingDate,
          startTime: groomingStart,
        };
        if (groomerId) grooming.groomerId = groomerId;
        base.grooming = grooming;
      }
      // Include intake answers on the final create call (not on preview-quote).
      if (includeIntake && Object.keys(answers).length > 0) {
        base.intakeResponses = { byPet: answers };
      }
      return base;
    },
    [
      categoryParam,
      serviceId,
      petIds,
      resolvedFacilityId,
      checkIn,
      checkout,
      runIdByPet,
      daycareDates,
      sessionType,
      groomingDate,
      groomingStart,
      groomerId,
      intakeAnswers,
    ],
  );

  const fetchPreviewQuote = useCallback(async () => {
    setSubmitError(null);
    setVaccineGate(null);
    const body = buildBookingRequestBody(false);
    if (!body) return;
    try {
      let r: QuoteSnapshot;
      if (petMode === 'guest' || !user) {
        // Public quote — inline pet descriptors instead of pet ids. Same
        // pricing engine as the signed-in route (server asserts parity).
        const { petIds: _ids, intakeResponses: _intake, ...rest } = body;
        r = await customerApi<QuoteSnapshot>('/api/customer/preview-quote', {
          method: 'POST',
          body: JSON.stringify({
            ...rest,
            pets:
              petMode === 'guest'
                ? guestPetsToDescriptors(guestPets)
                : selectedPets.map((p) => ({
                    name: p.name,
                    species: p.species,
                    sizeCategory: p.sizeCategory,
                    breed: null,
                  })),
          }),
        });
      } else {
        r = await customerApi<QuoteSnapshot>('/api/customer/bookings/preview-quote', {
          method: 'POST',
          body: JSON.stringify(body),
        });
      }
      setQuote(r);
    } catch (err) {
      if (err instanceof CustomerApiError) {
        const b = err.body as {
          error?: string;
          message?: string;
          petId?: string;
          vaccineId?: string;
          petName?: string;
        } | null;
        if (b?.error === 'vaccine_required' && b.petId && b.vaccineId) {
          setVaccineGate({
            petId: b.petId,
            vaccineId: b.vaccineId,
            ...(b.petName ? { petName: b.petName } : {}),
          });
        } else if (b?.error === 'assessment_required') {
          setSubmitError(
            b.message ??
              `${b.petName ?? 'This pet'} needs an evaluation day before this booking can be made.`,
          );
        } else if (b?.error === 'min_stay_not_met') {
          setSubmitError(b.message ?? 'This stay is shorter than the minimum for these dates.');
        } else if (b?.error) {
          setSubmitError(b.error);
        } else {
          setSubmitError(`Could not compute quote (HTTP ${err.status})`);
        }
      } else {
        setSubmitError('Network error computing quote');
      }
    }
  }, [buildBookingRequestBody, petMode, user, guestPets, selectedPets]);

  // Minimum-stay check on the boarding dates step. Re-runs the preview quote
  // whenever both dates are valid so the tenant's `min_stay` rule surfaces
  // inline ("Stays between 20 Dec and 3 Jan need at least 3 nights.") before
  // the customer moves on — the server refuses the same stay at create time.
  useEffect(() => {
    if (currentStep !== 'dates' || categoryParam !== 'boarding') return;
    if (!checkIn || !checkout || checkIn >= checkout || !serviceId || petIds.length === 0) {
      setMinStayMessage(null);
      return;
    }
    let cancelled = false;
    customerApi<QuoteSnapshot>('/api/customer/bookings/preview-quote', {
      method: 'POST',
      body: JSON.stringify({
        serviceCategory: 'boarding',
        primaryServiceId: serviceId,
        petIds,
        boarding: { checkIn, checkout },
      }),
    })
      .then(() => {
        if (!cancelled) setMinStayMessage(null);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof CustomerApiError) {
          const b = err.body as { error?: string; message?: string } | null;
          if (b?.error === 'min_stay_not_met') {
            setMinStayMessage(
              b.message ?? 'This stay is shorter than the minimum for the selected dates.',
            );
            return;
          }
        }
        // Any other failure is surfaced by the review step's quote — don't
        // block the dates step on it.
        setMinStayMessage(null);
      });
    return () => {
      cancelled = true;
    };
  }, [currentStep, categoryParam, checkIn, checkout, serviceId, petIds]);

  // Auto-fetch quote when entering review step
  useEffect(() => {
    if (currentStep === 'review' && !quote && !authLoading) {
      void fetchPreviewQuote();
    }
  }, [currentStep, quote, authLoading, fetchPreviewQuote]);

  // 11.4 — Payment step recovery: if we're on the payment step but in-memory
  // createdBooking is gone (reload / deep-link), redirect to the booking detail
  // page which already mounts PayBookingPanel for pending bookings.
  const orphanedBookingId = params.get('bookingId');
  useEffect(() => {
    if (currentStep === 'payment' && !createdBooking && orphanedBookingId) {
      navigate(`/account/bookings/${orphanedBookingId}`, { replace: true });
    }
  }, [currentStep, createdBooking, orphanedBookingId, navigate]);

  const createBooking = useCallback(
    async (overrides?: PetOverrides) => {
      if (creating) return;
      setSubmitError(null);
      setVaccineGate(null);
      const body = buildBookingRequestBody(true, overrides);
      if (!body) {
        setSubmitError('Missing wizard inputs');
        return;
      }
      setCreating(true);
      try {
        const r = await customerApi<CreateBookingResponse>('/api/customer/bookings', {
          method: 'POST',
          body: JSON.stringify(body),
        });
        setCreatedBooking(r);
        // Migration 0101 — the intake step's feeding plans belong to the pets,
        // not the booking; save them now that the pets definitely exist.
        await saveFeedingPlans(
          overrides?.petIds ?? petIds,
          overrides?.feedingPlanByPet ?? feedingPlanByPet,
        );
        if (isValidDraftId(draftId)) clearBookDraft(tenantKey(), draftId);
        // Branch on payment policy: no_payment / pay_later → straight to
        // confirmation. pay_now / deposit_then_balance → payment step.
        const primary = r.bookings.find((b) => b.id === r.primaryBookingId);
        if (primary?.paymentPolicy === 'no_payment' || primary?.paymentPolicy === 'pay_later') {
          navigate(`/book/${r.primaryBookingId}/confirmation`);
        } else {
          // When guest pets were just resolved to real ids, swap the URL over
          // to account mode in the same navigation so the step list stays
          // consistent (the `account` step disappears once pets are on file).
          const extra: Record<string, string> = { bookingId: r.primaryBookingId };
          if (overrides) extra.pets = overrides.petIds.join(',');
          goToStep('payment', extra);
        }
      } catch (err) {
        if (err instanceof CustomerApiError) {
          const b = err.body as {
            error?: string;
            petId?: string;
            vaccineId?: string;
            petName?: string;
            message?: string;
          } | null;
          if (b?.error === 'vaccine_required' && b.petId && b.vaccineId) {
            setVaccineGate({
              petId: b.petId,
              vaccineId: b.vaccineId,
              ...(b.petName ? { petName: b.petName } : {}),
            });
          } else if (b?.error === 'assessment_required') {
            setSubmitError(
              b.message ??
                `${b.petName ?? 'This pet'} needs an evaluation day before this booking can be made.`,
            );
          } else if (b?.error === 'min_stay_not_met') {
            setSubmitError(b.message ?? 'This stay is shorter than the minimum for these dates.');
          } else if (b?.error === 'capacity_full') {
            setSubmitError('Daycare is full for the selected date(s).');
          } else if (b?.error === 'run_unavailable') {
            setSubmitError(
              'That run is no longer available for these dates — please pick another.',
            );
          } else if (b?.error === 'customer_not_linked') {
            setSubmitError(
              "Your account isn't linked to a customer record at this kennel yet. Please contact the kennel directly to get set up, then try again.",
            );
          } else if (b?.error === 'booking_not_configured') {
            setSubmitError(
              'Online bookings are not available at this time. Please contact the kennel directly.',
            );
          } else {
            setSubmitError(
              b?.message ?? b?.error ?? `Could not create booking (HTTP ${err.status})`,
            );
          }
        } else {
          setSubmitError('Network error creating booking');
        }
      } finally {
        setCreating(false);
      }
    },
    [
      creating,
      buildBookingRequestBody,
      navigate,
      draftId,
      saveFeedingPlans,
      petIds,
      feedingPlanByPet,
    ],
  );

  /**
   * Post-sign-in pet resolution: each inline pet is either matched to a pet
   * already on file or created via the batch endpoint, then the booking is
   * created with the real ids. Intake answers + run picks keyed by the guest
   * keys are re-keyed to the real ids on the way through.
   */
  const resolveGuestPetsAndBook = useCallback(
    async (mapping: Record<string, string | null>) => {
      setSubmitError(null);
      const toCreate = guestPets.filter((p) => !mapping[p.key]);
      const realIdByKey: Record<string, string> = {};
      for (const p of guestPets) {
        const existing = mapping[p.key];
        if (existing) realIdByKey[p.key] = existing;
      }
      if (toCreate.length > 0) {
        setCreating(true);
        try {
          const r = await customerApi<{ pets: Array<{ id: string; name: string }> }>(
            '/api/customer/pets/batch',
            {
              method: 'POST',
              body: JSON.stringify({
                pets: toCreate.map((p) => ({
                  name: p.name.trim() || 'Pet',
                  species: p.species,
                  ...(p.sizeCategory ? { sizeCategory: p.sizeCategory } : {}),
                  ...(p.breed.trim() ? { breed: p.breed.trim() } : {}),
                })),
              }),
            },
          );
          toCreate.forEach((p, i) => {
            const created = r.pets[i];
            if (created) realIdByKey[p.key] = created.id;
          });
        } catch (err) {
          setSubmitError(
            err instanceof CustomerApiError
              ? `Could not save your pets (HTTP ${err.status})`
              : 'Network error saving your pets',
          );
          setCreating(false);
          return;
        } finally {
          setCreating(false);
        }
      }
      const ids = guestPets.map((p) => realIdByKey[p.key]).filter((x): x is string => !!x);
      if (ids.length !== guestPets.length) {
        setSubmitError('Could not resolve all pets — please try again.');
        return;
      }
      const remappedRuns: Record<string, string | null> = {};
      const remappedAnswers: IntakeAnswers = {};
      const remappedFeeding: Record<string, FeedingPlanDraft> = {};
      for (const p of guestPets) {
        const real = realIdByKey[p.key] as string;
        remappedRuns[real] = runIdByPet[p.key] ?? null;
        const a = intakeAnswers[p.key];
        if (a) remappedAnswers[real] = a;
        const f = feedingPlanByPet[p.key];
        if (f) remappedFeeding[real] = f;
      }
      await createBooking({
        petIds: ids,
        runIdByPet: remappedRuns,
        intakeAnswers: remappedAnswers,
        feedingPlanByPet: remappedFeeding,
      });
    },
    [guestPets, runIdByPet, intakeAnswers, feedingPlanByPet, createBooking],
  );

  // Magic-link callback: bring the customer straight back to the account step
  // of THIS wizard (works for /book and /embed/book alike).
  const returnTo = useMemo(() => {
    const p = new URLSearchParams(params);
    p.set('step', 'account');
    return buildReturnTo(location.pathname, p);
  }, [params, location.pathname]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  if (loadError) {
    return (
      <BrandedShell>
        <div className="mx-auto max-w-2xl px-4 py-6 sm:px-6 sm:py-10">
          <h1 className="text-3xl font-medium">Booking unavailable</h1>
          <p className="mt-4 text-muted-foreground">{loadError}</p>
        </div>
      </BrandedShell>
    );
  }

  const showGuestPetsForm =
    petMode === 'guest' || (!authLoading && !user) || (pets !== null && pets.length === 0);

  return (
    <BrandedShell>
      <div className="mx-auto max-w-2xl px-4 py-6 sm:px-6 sm:py-10">
        <header className="mb-8">
          <h1 className="text-3xl font-medium tracking-tight">Book a stay</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Step <span className="num">{stepIndex + 1}</span> of{' '}
            <span className="num">{totalSteps}</span>: <StepLabel step={currentStep ?? 'service'} />
          </p>
          {!authLoading && !user && currentStep !== 'account' && currentStep !== 'payment' && (
            <p className="mt-1 text-xs text-muted-foreground">
              No account needed to check availability and prices — you&apos;ll sign in only when you
              confirm.
            </p>
          )}
        </header>

        <div className="rounded-lg border border-border bg-card p-6">
          {currentStep === 'service' && (
            <ServiceStep services={services} onSelect={selectCategory} />
          )}

          {currentStep === 'pets' &&
            (authLoading ? (
              <Skeleton lines={3} />
            ) : showGuestPetsForm ? (
              <GuestPetsStep
                pets={guestPets}
                onSubmit={confirmGuestPets}
                onBack={goBack}
                onUseAccountPets={
                  user && pets && pets.length > 0 ? () => switchPetMode('account') : undefined
                }
              />
            ) : (
              <PetsStep
                pets={pets}
                selectedIds={petIds}
                blockReason={(p) => assessmentBlockReason(p, categoryParam, assessmentGate)}
                onSubmit={confirmPets}
                onBack={goBack}
                onDescribeNewPet={() => switchPetMode('guest')}
              />
            ))}

          {currentStep === 'dates' && categoryParam === 'boarding' && (
            <BoardingDatesStep
              checkIn={checkIn}
              checkout={checkout}
              facilityId={resolvedFacilityId}
              petDescriptors={petDescriptorParam}
              minStayMessage={minStayMessage}
              onChange={(ci, co) => {
                setCheckIn(ci);
                setCheckOut(co);
                setQuote(null);
              }}
              onNext={() => {
                const next = new URLSearchParams(params);
                next.set('checkIn', checkIn);
                next.set('checkout', checkout);
                next.set('step', 'run');
                setParams(next);
              }}
              onBack={goBack}
            />
          )}

          {currentStep === 'run' && categoryParam === 'boarding' && (
            <RunPickerStep
              selectedPets={selectedPets}
              runIdByPet={runIdByPet}
              onChange={setRunIdByPet}
              onNext={() => goToStep('intake')}
              onBack={goBack}
            />
          )}

          {currentStep === 'daycare_dates' && categoryParam === 'daycare' && (
            <DaycareDatesStep
              dates={daycareDates}
              onChange={(d) => {
                setDaycareDates(d);
                setQuote(null);
                const next = new URLSearchParams(params);
                next.set('daycareDates', d.join(','));
                setParams(next, { replace: true });
              }}
              onNext={() => goToStep('session')}
              onBack={goBack}
            />
          )}
          {currentStep === 'daycare_dates' && categoryParam === 'daycare' && serviceId && (
            <RecurringDaycarePanel
              serviceId={serviceId}
              petIds={petIds}
              facilityId={resolvedFacilityId}
              defaultSessionType={sessionType}
            />
          )}

          {currentStep === 'session' && categoryParam === 'daycare' && (
            <SessionTypeStep
              sessionType={sessionType}
              dates={daycareDates}
              facilityId={resolvedFacilityId}
              petSizeCategory={selectedPets[0]?.sizeCategory ?? null}
              onChange={(s) => {
                setSessionType(s);
                setQuote(null);
                const next = new URLSearchParams(params);
                next.set('sessionType', s);
                setParams(next, { replace: true });
              }}
              onNext={() => goToStep('intake')}
              onBack={goBack}
            />
          )}

          {currentStep === 'grooming_date' && categoryParam === 'grooming' && (
            <GroomingDateStep
              date={groomingDate}
              onChange={(d) => {
                setGroomingDate(d);
                setQuote(null);
                const next = new URLSearchParams(params);
                next.set('groomingDate', d);
                setParams(next, { replace: true });
              }}
              onNext={() => goToStep('groomer')}
              onBack={goBack}
            />
          )}

          {currentStep === 'groomer' && categoryParam === 'grooming' && (
            <GroomerPickerStep
              serviceId={serviceId}
              groomerId={groomerId}
              onChange={(g) => {
                setGroomerId(g);
                setQuote(null);
                const next = new URLSearchParams(params);
                if (g) next.set('groomerId', g);
                else next.delete('groomerId');
                setParams(next, { replace: true });
              }}
              onNext={() => goToStep('slot')}
              onBack={goBack}
            />
          )}

          {currentStep === 'slot' && categoryParam === 'grooming' && (
            <SlotPickerStep
              serviceId={serviceId}
              service={selectedService}
              date={groomingDate}
              groomerId={groomerId}
              startTime={groomingStart}
              onChange={(t) => {
                setGroomingStart(t);
                const next = new URLSearchParams(params);
                next.set('startTime', t);
                setParams(next, { replace: true });
              }}
              onNext={() => goToStep('intake')}
              onBack={goBack}
            />
          )}

          {currentStep === 'intake' && (
            <IntakeStep
              serviceCategory={categoryParam}
              petIds={petIds}
              selectedPets={selectedPets}
              guestSpecies={petMode === 'guest' || !user ? guestSpecies : null}
              answers={intakeAnswers}
              onChange={setIntakeAnswers}
              feedingPlans={feedingPlanByPet}
              onFeedingPlansChange={setFeedingPlanByPet}
              onNext={() => goToStep('review')}
              onBack={goBack}
            />
          )}

          {currentStep === 'review' && (
            <ReviewStep
              quote={quote}
              selectedService={selectedService}
              selectedPets={selectedPets}
              bookingSummary={(() => {
                if (categoryParam === 'boarding' && checkIn && checkout)
                  return `${formatDate(checkIn.slice(0, 10))} → ${formatDate(checkout.slice(0, 10))}`;
                if (categoryParam === 'daycare' && daycareDates.length > 0)
                  return daycareDates.length === 1
                    ? formatDate(daycareDates[0] as string)
                    : `${daycareDates.length} dates`;
                if (categoryParam === 'grooming' && groomingDate)
                  return groomingStart
                    ? `${formatDate(groomingDate)} at ${groomingStart.slice(11, 16)}`
                    : formatDate(groomingDate);
                return null;
              })()}
              vaccineGate={vaccineGate}
              submitError={submitError}
              discountCode={discountCode}
              onDiscountCodeChange={setDiscountCode}
              onRetryQuote={fetchPreviewQuote}
              onConfirm={() => {
                if (needsAccountStep) goToStep('account');
                else void createBooking();
              }}
              confirmLabel={
                !user ? 'Sign in to book' : petMode === 'guest' ? 'Continue' : 'Confirm booking'
              }
              signedIn={!!user}
              confirming={creating}
              onBack={goBack}
            />
          )}

          {currentStep === 'account' &&
            (authLoading ? (
              <Skeleton lines={3} />
            ) : !user ? (
              <SignInStep callbackURL={returnTo} onBack={goBack} />
            ) : petMode === 'guest' ? (
              <MatchPetsStep
                guestPets={guestPets}
                accountPets={pets}
                submitting={creating}
                error={submitError}
                onConfirm={(mapping) => void resolveGuestPetsAndBook(mapping)}
                onBack={goBack}
              />
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  You&apos;re signed in. Confirm to create your booking.
                </p>
                {submitError && <p className="text-sm text-destructive">{submitError}</p>}
                <NavRow
                  onBack={goBack}
                  onNext={() => void createBooking()}
                  nextLabel={creating ? 'Confirming…' : 'Confirm booking'}
                  nextDisabled={creating}
                />
              </div>
            ))}

          {currentStep === 'payment' && !createdBooking && orphanedBookingId && (
            // Redirect fires via useEffect above; show a brief transitional message.
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Redirecting to payment…</p>
            </div>
          )}

          {currentStep === 'payment' && createdBooking && (
            <PaymentStep
              booking={createdBooking}
              {...(discountCode ? { discountCode } : {})}
              onPaid={() => navigate(`/book/${createdBooking.primaryBookingId}/confirmation`)}
            />
          )}
        </div>

        <div className="mt-6 text-xs text-muted-foreground">
          Want to book by phone instead? Contact us — we&apos;ll help you straight away.
        </div>
      </div>
    </BrandedShell>
  );
}

// ---------------------------------------------------------------------------
// Step components
// ---------------------------------------------------------------------------

function ServiceStep({
  services,
  onSelect,
}: {
  services: ServiceLite[] | null;
  onSelect: (cat: ServiceCategory, serviceId: string) => void;
}) {
  if (services === null) return <Skeleton lines={4} />;
  if (services.length === 0) {
    return (
      <div>
        <p className="text-sm text-muted-foreground">
          No services are configured yet. The kennel will need to add at least one service before
          customers can book online.
        </p>
      </div>
    );
  }
  const grouped: Record<ServiceCategory, ServiceLite[]> = {
    boarding: services.filter((s) => s.category === 'boarding'),
    daycare: services.filter((s) => s.category === 'daycare'),
    grooming: services.filter((s) => s.category === 'grooming'),
  };
  return (
    <div className="space-y-6">
      {(['boarding', 'daycare', 'grooming'] as const).map((cat) => {
        const list = grouped[cat];
        if (list.length === 0) return null;
        return (
          <section key={cat}>
            <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-muted-foreground">
              {cat}
            </h2>
            <div className="space-y-2">
              {list.map((svc) => (
                <button
                  key={svc.id}
                  type="button"
                  onClick={() => onSelect(cat, svc.id)}
                  className="w-full rounded-md border border-border p-4 text-left transition hover:border-primary"
                >
                  <div className="font-medium">{svc.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {svc.defaultPriceMinor > 0 ? (
                      <>
                        From{' '}
                        <span className="num">
                          {formatMoney(svc.defaultPriceMinor, svc.currency)}
                        </span>
                      </>
                    ) : (
                      'See pricing'
                    )}
                    {' · '}
                    {svc.requiresApproval ? 'Approval required' : policyLabel(svc.paymentPolicy)}
                  </div>
                </button>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function PetsStep({
  pets,
  selectedIds,
  blockReason,
  onSubmit,
  onBack,
  onDescribeNewPet,
}: {
  pets: PetLite[] | null;
  selectedIds: string[];
  /** Migration 0099 — evaluation gate: non-null = this pet cannot be booked. */
  blockReason?: (pet: PetLite) => string | null;
  onSubmit: (ids: string[]) => void;
  onBack: () => void;
  onDescribeNewPet: () => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set(selectedIds));
  if (pets === null) return <Skeleton lines={3} />;
  function togglePet(id: string) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  }
  return (
    <div>
      <p className="mb-3 text-sm text-muted-foreground">Which pet(s) is this booking for?</p>
      <div className="space-y-2">
        {pets.map((p) => {
          const blocked = blockReason?.(p) ?? null;
          return (
            <div
              key={p.id}
              className={`rounded-md border p-3 ${blocked ? 'border-border bg-muted/40' : 'border-border'}`}
            >
              <label className="flex cursor-pointer items-center gap-3">
                <input
                  type="checkbox"
                  checked={selected.has(p.id)}
                  disabled={blocked !== null}
                  aria-label={p.name}
                  onChange={() => togglePet(p.id)}
                />
                <span className="font-medium">{p.name}</span>
                <span className="text-xs text-muted-foreground">{p.species}</span>
              </label>
              {blocked && (
                <p
                  className="mt-2 text-xs text-muted-foreground"
                  data-testid={`assessment-blocker-${p.id}`}
                >
                  {blocked}{' '}
                  {p.assessmentStatus !== 'scheduled' && p.assessmentStatus !== 'failed' && (
                    <Link
                      to={`/account/pets/${p.id}/assessment`}
                      className="font-medium text-primary hover:underline"
                    >
                      Book an evaluation day
                    </Link>
                  )}
                </p>
              )}
            </div>
          );
        })}
      </div>
      <button
        type="button"
        onClick={onDescribeNewPet}
        className="mt-3 inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
      >
        <Plus size={14} strokeWidth={1.75} />
        Booking for a pet that isn&apos;t listed?
      </button>
      <NavRow
        onBack={onBack}
        nextLabel={`Continue with ${selected.size} pet${selected.size === 1 ? '' : 's'}`}
        nextDisabled={selected.size === 0}
        onNext={() => onSubmit(Array.from(selected))}
      />
    </div>
  );
}

const SPECIES_OPTIONS: ReadonlyArray<{ value: GuestSpecies; label: string }> = [
  { value: 'dog', label: 'Dog' },
  { value: 'cat', label: 'Cat' },
];
const SIZE_OPTIONS: ReadonlyArray<{ value: GuestSizeCategory | ''; label: string }> = [
  { value: '', label: 'Not sure' },
  { value: 'small', label: 'Small (up to ~10 kg)' },
  { value: 'medium', label: 'Medium (~10–25 kg)' },
  { value: 'large', label: 'Large (~25–45 kg)' },
  { value: 'extra_large', label: 'Extra large (45 kg+)' },
];

function newGuestPet(): GuestPet {
  return { key: newDraftId().slice(0, 12), name: '', species: 'dog', sizeCategory: '', breed: '' };
}

/**
 * Inline pet descriptions for visitors with no pets on file. Name, species
 * and a size band are all the availability + quote engines need; breed is
 * optional and carried through to the pet record created after sign-in.
 */
function GuestPetsStep({
  pets,
  onSubmit,
  onBack,
  onUseAccountPets,
}: {
  pets: GuestPet[];
  onSubmit: (pets: GuestPet[]) => void;
  onBack: () => void;
  onUseAccountPets?: (() => void) | undefined;
}) {
  const [list, setList] = useState<GuestPet[]>(pets.length > 0 ? pets : [newGuestPet()]);
  const ready = list.length > 0 && list.every((p) => p.name.trim().length > 0);

  function update(key: string, patch: Partial<GuestPet>) {
    setList((cur) => cur.map((p) => (p.key === key ? { ...p, ...patch } : p)));
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Tell us a little about who&apos;s coming to stay. You can add more than one pet.
      </p>
      <div className="space-y-3">
        {list.map((p, i) => (
          <fieldset key={p.key} className="space-y-3 rounded-md border border-border p-3">
            <legend className="px-1 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
              Pet <span className="num">{i + 1}</span>
            </legend>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex flex-col text-sm">
                <span className="mb-1 font-medium">Name</span>
                <input
                  type="text"
                  value={p.name}
                  maxLength={80}
                  aria-label={`Pet ${i + 1} name`}
                  onChange={(e) => update(p.key, { name: e.target.value })}
                  className="rounded-md border border-border bg-surface px-3 py-2"
                />
              </label>
              <label className="flex flex-col text-sm">
                <span className="mb-1 font-medium">Species</span>
                <select
                  value={p.species}
                  aria-label={`Pet ${i + 1} species`}
                  onChange={(e) => update(p.key, { species: e.target.value as GuestSpecies })}
                  className="rounded-md border border-border bg-surface px-3 py-2"
                >
                  {SPECIES_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col text-sm">
                <span className="mb-1 font-medium">Size</span>
                <select
                  value={p.sizeCategory}
                  aria-label={`Pet ${i + 1} size`}
                  onChange={(e) =>
                    update(p.key, { sizeCategory: e.target.value as GuestSizeCategory | '' })
                  }
                  className="rounded-md border border-border bg-surface px-3 py-2"
                >
                  {SIZE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col text-sm">
                <span className="mb-1 font-medium">
                  Breed <span className="font-normal text-muted-foreground">(optional)</span>
                </span>
                <input
                  type="text"
                  value={p.breed}
                  maxLength={80}
                  aria-label={`Pet ${i + 1} breed`}
                  onChange={(e) => update(p.key, { breed: e.target.value })}
                  className="rounded-md border border-border bg-surface px-3 py-2"
                />
              </label>
            </div>
            {list.length > 1 && (
              <button
                type="button"
                onClick={() => setList((cur) => cur.filter((x) => x.key !== p.key))}
                className="inline-flex items-center gap-1.5 text-xs text-destructive hover:underline"
              >
                <Trash2 size={13} strokeWidth={1.75} />
                Remove
              </button>
            )}
          </fieldset>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <button
          type="button"
          disabled={list.length >= 10}
          onClick={() => setList((cur) => [...cur, newGuestPet()])}
          className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline disabled:opacity-50"
        >
          <Plus size={14} strokeWidth={1.75} />
          Add another pet
        </button>
        {onUseAccountPets && (
          <button
            type="button"
            onClick={onUseAccountPets}
            className="text-sm text-muted-foreground hover:underline"
          >
            Choose from my pets instead
          </button>
        )}
      </div>
      <NavRow
        onBack={onBack}
        nextLabel={`Continue with ${list.length} pet${list.length === 1 ? '' : 's'}`}
        nextDisabled={!ready}
        onNext={() => onSubmit(list.map((p) => ({ ...p, name: p.name.trim() })))}
      />
    </div>
  );
}

interface BoardingAvailability {
  available: boolean;
  sizeCategoriesAvailable: string[];
  unavailableSpecies: string[];
}

function BoardingDatesStep({
  checkIn,
  checkout,
  facilityId,
  petDescriptors,
  minStayMessage,
  onChange,
  onNext,
  onBack,
}: {
  checkIn: string;
  checkout: string;
  facilityId: string;
  petDescriptors: string;
  /** Inline minimum-stay violation for the chosen dates (null = bookable). */
  minStayMessage: string | null;
  onChange: (ci: string, co: string) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  const ready = checkIn !== '' && checkout !== '' && checkIn < checkout && minStayMessage === null;
  // Derived-loaded availability: store the probed inputs alongside the result
  // so a stale answer never renders against newer dates.
  const [availability, setAvailability] = useState<{
    key: string;
    result: BoardingAvailability | null;
  } | null>(null);
  const probeKey = ready ? `${checkIn}|${checkout}|${petDescriptors}|${facilityId}` : '';

  useEffect(() => {
    if (!probeKey || !petDescriptors) return;
    let cancelled = false;
    const qs = new URLSearchParams({
      vertical: 'boarding',
      checkIn,
      checkout,
      pets: petDescriptors,
    });
    if (facilityId) qs.set('facilityId', facilityId);
    customerApi<BoardingAvailability>(`/api/customer/availability?${qs.toString()}`)
      .then((r) => {
        if (!cancelled) setAvailability({ key: probeKey, result: r });
      })
      .catch(() => {
        // Probe failure never blocks the customer — the server re-checks on commit.
        if (!cancelled) setAvailability({ key: probeKey, result: null });
      });
    return () => {
      cancelled = true;
    };
  }, [probeKey, checkIn, checkout, petDescriptors, facilityId]);

  const current = availability && availability.key === probeKey ? availability.result : undefined;
  const probing = ready && petDescriptors !== '' && current === undefined;
  const unavailable = current !== undefined && current !== null && !current.available;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Pick check-in and pick-up dates.</p>
      {minStayMessage !== null && (
        <p
          role="alert"
          className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {minStayMessage}
        </p>
      )}
      <div className="grid grid-cols-2 gap-3">
        <label className="flex flex-col text-sm">
          <span className="mb-1 font-medium">Check-in</span>
          <input
            type="date"
            value={checkIn}
            onChange={(e) => onChange(e.target.value, checkout)}
            className="rounded-md border border-border bg-surface px-3 py-2"
          />
        </label>
        <label className="flex flex-col text-sm">
          <span className="mb-1 font-medium">Check-out</span>
          <input
            type="date"
            value={checkout}
            onChange={(e) => onChange(checkIn, e.target.value)}
            className="rounded-md border border-border bg-surface px-3 py-2"
          />
        </label>
      </div>
      {ready && (
        <div role="status" className="text-sm">
          {probing && <span className="text-muted-foreground">Checking availability…</span>}
          {current && current.available && (
            <span className="inline-flex items-center gap-1.5 text-success">
              <Check size={15} strokeWidth={3} />
              Space available for these dates
            </span>
          )}
          {unavailable && (
            <span className="inline-flex items-center gap-1.5 text-destructive">
              <CircleAlert size={15} strokeWidth={1.75} />
              {current && current.unavailableSpecies.length > 0
                ? `Sorry — we can't take ${current.unavailableSpecies.join(' or ')}s here.`
                : "Sorry — we're full for these dates. Try different dates."}
            </span>
          )}
        </div>
      )}
      <NavRow onBack={onBack} onNext={onNext} nextDisabled={!ready || unavailable} />
    </div>
  );
}

function RunPickerStep({
  selectedPets,
  runIdByPet,
  onChange,
  onNext,
  onBack,
}: {
  selectedPets: PetLite[];
  runIdByPet: Record<string, string | null>;
  onChange: (m: Record<string, string | null>) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  const noun = useSpaceNoun();
  const [runs, setRuns] = useState<RunLite[] | null>(null);
  const [facilityId, setFacilityId] = useState<string | null>(null);

  // Load the tenant's active facilities, then auto-select the first one.
  // Single-facility tenants get the facility resolved transparently. Multi-
  // facility tenants need an upstream facility picker (v1.x follow-up).
  useEffect(() => {
    customerApi<{ facilities: Array<{ id: string; name: string; timezone: string }> }>(
      '/api/customer/facilities',
    )
      .then((r) => {
        const first = r.facilities[0];
        if (first) setFacilityId(first.id);
        else setRuns([]);
      })
      .catch(() => {
        setFacilityId(null);
        setRuns([]);
      });
  }, []);

  // Load runs once we have a facilityId.
  useEffect(() => {
    if (!facilityId) return;
    customerApi<{ runs: RunLite[] }>(`/api/customer/runs?facilityId=${facilityId}`)
      .then((r) => setRuns(r.runs))
      .catch(() => setRuns([]));
  }, [facilityId]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Pick a preferred {noun.singular}, or leave as "Any {noun.singular}".
      </p>
      {selectedPets.map((pet) => {
        // A run only takes the species it was set up for — offer just those
        // (the API refuses the rest with 422 run_species_mismatch).
        const forPet = (runs ?? []).filter((r) => r.speciesAllowed.includes(pet.species));
        return (
          <div key={pet.id} className="rounded-md border border-border p-3">
            <p className="mb-2 text-sm font-medium">{pet.name}</p>
            <select
              value={runIdByPet[pet.id] ?? ''}
              onChange={(e) => onChange({ ...runIdByPet, [pet.id]: e.target.value || null })}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
            >
              <option value="">Any {noun.singular}</option>
              {forPet.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name} ({r.sizeCategory})
                </option>
              ))}
            </select>
          </div>
        );
      })}
      <NavRow onBack={onBack} onNext={onNext} />
    </div>
  );
}

function DaycareDatesStep({
  dates,
  onChange,
  onNext,
  onBack,
}: {
  dates: string[];
  onChange: (d: string[]) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  const [input, setInput] = useState('');
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Pick one or more daycare dates (within the next 90 days).
      </p>
      <div className="flex gap-2">
        <input
          type="date"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          className="flex-1 rounded-md border border-border bg-surface px-3 py-2 text-sm"
        />
        <button
          type="button"
          disabled={!input || dates.includes(input)}
          onClick={() => {
            onChange([...dates, input].sort());
            setInput('');
          }}
          className="rounded-md border border-border bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-50"
        >
          Add
        </button>
      </div>
      {dates.length > 0 && (
        <div className="space-y-2">
          {dates.map((d) => (
            <div
              key={d}
              className="flex items-center justify-between rounded-md border border-border p-2"
            >
              <span className="num text-sm">{formatDate(d)}</span>
              <button
                type="button"
                onClick={() => onChange(dates.filter((x) => x !== d))}
                className="text-xs text-destructive hover:underline"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
      <NavRow onBack={onBack} onNext={onNext} nextDisabled={dates.length === 0} />
    </div>
  );
}

function SessionTypeStep({
  sessionType,
  dates,
  facilityId,
  petSizeCategory,
  onChange,
  onNext,
  onBack,
}: {
  sessionType: 'full_day' | 'half_day_am' | 'half_day_pm';
  dates: string[];
  facilityId: string;
  petSizeCategory: string | null;
  onChange: (s: 'full_day' | 'half_day_am' | 'half_day_pm') => void;
  onNext: () => void;
  onBack: () => void;
}) {
  const [capacity, setCapacity] = useState<Record<string, boolean>>({});

  // Probe capacity for the first date for each session type as a quick filter.
  useEffect(() => {
    if (dates.length === 0 || !facilityId) return;
    const first = dates[0];
    const types = ['full_day', 'half_day_am', 'half_day_pm'];
    Promise.all(
      types.map((t) =>
        customerApi<{ ok: boolean }>(
          `/api/customer/daycare/capacity?facilityId=${encodeURIComponent(facilityId)}&date=${first}&sessionType=${t}${
            petSizeCategory ? `&petSizeCategory=${petSizeCategory}` : ''
          }`,
        )
          .then((r) => [t, r.ok] as const)
          .catch(() => [t, true] as const),
      ),
    ).then((pairs) => {
      const map: Record<string, boolean> = {};
      for (const [t, ok] of pairs) map[t] = ok;
      setCapacity(map);
    });
  }, [dates, facilityId, petSizeCategory]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Choose a daycare session length.</p>
      <div className="grid gap-3">
        {(
          [
            { value: 'full_day', label: 'Full day' },
            { value: 'half_day_am', label: 'Half day (morning)' },
            { value: 'half_day_pm', label: 'Half day (afternoon)' },
          ] as const
        ).map((opt) => {
          const ok = capacity[opt.value] !== false;
          return (
            <label
              key={opt.value}
              className={`flex cursor-pointer items-center gap-3 rounded-md border p-3 ${
                sessionType === opt.value ? 'border-primary' : 'border-border'
              } ${!ok ? 'opacity-50' : ''}`}
            >
              <input
                type="radio"
                checked={sessionType === opt.value}
                onChange={() => onChange(opt.value)}
                disabled={!ok}
              />
              <span className="font-medium">{opt.label}</span>
              {!ok && <span className="ml-auto text-xs text-muted-foreground">Full</span>}
            </label>
          );
        })}
      </div>
      <NavRow onBack={onBack} onNext={onNext} />
    </div>
  );
}

function GroomingDateStep({
  date,
  onChange,
  onNext,
  onBack,
}: {
  date: string;
  onChange: (d: string) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">When would you like to come in?</p>
      <input
        type="date"
        value={date}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-border bg-surface px-3 py-2"
      />
      <NavRow onBack={onBack} onNext={onNext} nextDisabled={!date} />
    </div>
  );
}

function GroomerPickerStep({
  serviceId,
  groomerId,
  onChange,
  onNext,
  onBack,
}: {
  serviceId: string;
  groomerId: string | null;
  onChange: (g: string | null) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  const [groomers, setGroomers] = useState<GroomerLite[] | null>(null);
  useEffect(() => {
    customerApi<{ groomers: GroomerLite[] }>(
      `/api/customer/groomers?serviceId=${encodeURIComponent(serviceId)}`,
    )
      .then((r) => setGroomers(r.groomers))
      .catch(() => setGroomers([]));
  }, [serviceId]);

  if (groomers === null) return <Skeleton lines={3} />;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Pick a groomer, or leave as "Any available".</p>
      <select
        value={groomerId ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        className="w-full rounded-md border border-border bg-surface px-3 py-2"
      >
        <option value="">Any available</option>
        {groomers.map((g) => (
          <option key={g.id} value={g.id}>
            {g.displayName}
          </option>
        ))}
      </select>
      <NavRow onBack={onBack} onNext={onNext} />
    </div>
  );
}

function SlotPickerStep({
  serviceId,
  service,
  date,
  groomerId,
  startTime,
  onChange,
  onNext,
  onBack,
}: {
  serviceId: string;
  service: ServiceLite | null;
  date: string;
  groomerId: string | null;
  startTime: string;
  onChange: (t: string) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  const [slots, setSlots] = useState<Record<string, Slot[]> | null>(null);
  const [facilityId, setFacilityId] = useState<string>('');
  const durationMin = service?.defaultDurationMin ?? 60;

  // Resolve facilityId from the customer-facing facilities endpoint. Auto-
  // selects the first active facility; multi-facility picker is a v1.x item.
  useEffect(() => {
    customerApi<{ facilities: Array<{ id: string }> }>('/api/customer/facilities')
      .then((r) => {
        const first = r.facilities[0];
        if (first) setFacilityId(first.id);
        else setSlots({});
      })
      .catch(() => setSlots({}));
  }, []);

  useEffect(() => {
    if (!facilityId || !date) return;
    const url = `/api/customer/grooming/slots?facilityId=${facilityId}&date=${date}&durationMin=${durationMin}&serviceId=${serviceId}${
      groomerId ? `&groomerId=${groomerId}` : ''
    }`;
    customerApi<{ slots: Record<string, Slot[]> }>(url)
      .then((r) => setSlots(r.slots))
      .catch(() => setSlots({}));
  }, [facilityId, date, durationMin, serviceId, groomerId]);

  // Flatten all groomers' slots into a single time-ordered list.
  const allSlots: Slot[] = useMemo(() => {
    if (!slots) return [];
    const out: Slot[] = [];
    for (const list of Object.values(slots)) out.push(...list);
    out.sort((a, b) => a.startAt.localeCompare(b.startAt));
    return out;
  }, [slots]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Pick a time slot.</p>
      {slots === null ? (
        <p className="text-sm text-muted-foreground">Loading available slots…</p>
      ) : allSlots.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No slots available — try a different date or groomer.
        </p>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {allSlots.map((s) => {
            const time = s.startAt.slice(11, 16);
            return (
              <button
                key={s.startAt}
                type="button"
                onClick={() => onChange(time)}
                className={`num rounded-md border px-3 py-2 text-sm ${
                  startTime === time
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border'
                }`}
              >
                {time}
              </button>
            );
          })}
        </div>
      )}
      <NavRow onBack={onBack} onNext={onNext} nextDisabled={!startTime} />
    </div>
  );
}

function IntakeStep({
  serviceCategory,
  petIds,
  selectedPets,
  guestSpecies,
  answers,
  onChange,
  feedingPlans,
  onFeedingPlansChange,
  onNext,
  onBack,
}: {
  serviceCategory: ServiceCategory | null;
  petIds: string[];
  selectedPets: PetLite[];
  /** When set, the public (species-based) question lookup is used. */
  guestSpecies: string[] | null;
  answers: IntakeAnswers;
  onChange: (a: IntakeAnswers) => void;
  /** Migration 0101 — per-pet feeding plan drafts (boarding + daycare only). */
  feedingPlans?: Record<string, FeedingPlanDraft>;
  onFeedingPlansChange?: (plans: Record<string, FeedingPlanDraft>) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  const [questions, setQuestions] = useState<IntakeQuestion[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const speciesKey = guestSpecies ? guestSpecies.join(',') : null;
  const collectFeeding =
    !!onFeedingPlansChange && (serviceCategory === 'boarding' || serviceCategory === 'daycare');
  const feedingIssues = collectFeeding
    ? selectedPets.flatMap((p) => {
        const plan = feedingPlans?.[p.id];
        return plan && draftHasContent(plan) ? validateFeedingDraft(plan) : [];
      })
    : [];
  const feedingSection = collectFeeding ? (
    <div className="space-y-3" data-testid="wizard-feeding-section">
      <div>
        <h3 className="text-sm font-medium">Feeding</h3>
        <p className="text-xs text-muted-foreground">
          What, how much and when. Optional now — you can edit it any time from your account.
        </p>
      </div>
      {selectedPets.map((pet) => (
        <div key={`feeding-${pet.id}`} className="space-y-2 rounded-md border border-border p-3">
          {selectedPets.length > 1 && (
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {pet.name}
            </p>
          )}
          <FeedingPlanEditor
            compact
            idPrefix={`wizard-${pet.id}`}
            value={feedingPlans?.[pet.id] ?? EMPTY_FEEDING_DRAFT}
            onChange={(next) => onFeedingPlansChange?.({ ...(feedingPlans ?? {}), [pet.id]: next })}
          />
        </div>
      ))}
      {feedingIssues.length > 0 && (
        <p className="text-xs text-destructive">{feedingIssues.join('. ')}</p>
      )}
    </div>
  ) : null;

  useEffect(() => {
    if (!serviceCategory || petIds.length === 0) return;
    const url =
      speciesKey !== null
        ? `/api/customer/intake-questions/applicable-guest?service=${serviceCategory}&species=${speciesKey}`
        : `/api/customer/intake-questions/applicable?service=${serviceCategory}&petIds=${petIds.join(',')}`;
    customerApi<{ questions: IntakeQuestion[] }>(url)
      .then((r) => setQuestions(r.questions))
      .catch(() => {
        setLoadError(true);
        setQuestions([]);
      });
  }, [serviceCategory, petIds, speciesKey]);

  if (questions === null) return <Skeleton lines={3} />;
  if (questions.length === 0) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {loadError
            ? 'Could not load intake questions; you can continue.'
            : 'No additional questions for this booking.'}
        </p>
        {feedingSection}
        <NavRow onBack={onBack} onNext={onNext} nextDisabled={feedingIssues.length > 0} />
      </div>
    );
  }

  const requiredAnswered = questions
    .filter((q) => q.required)
    .every((q) => selectedPets.every((p) => answers[p.id]?.[q.slug] !== undefined));

  return (
    <div className="space-y-4">
      {selectedPets.map((pet) => (
        <div key={pet.id} className="space-y-3 rounded-md border border-border p-3">
          {selectedPets.length > 1 && (
            <h3 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
              {pet.name}
            </h3>
          )}
          {questions.map((q) => {
            const current = answers[pet.id]?.[q.slug];
            return (
              <div key={`${pet.id}:${q.slug}`} className="rounded-md border border-border p-3">
                <p className="text-sm font-medium">
                  {q.label}
                  {q.required && <span className="ml-1 text-destructive">*</span>}
                </p>
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      onChange({
                        ...answers,
                        [pet.id]: {
                          ...(answers[pet.id] ?? {}),
                          [q.slug]: { answer: 1 },
                        },
                      })
                    }
                    className={`flex-1 rounded-md border px-3 py-2 text-sm ${
                      current?.answer === 1
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border'
                    }`}
                  >
                    {q.isAcknowledgement ? 'I confirm' : 'Yes'}
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      onChange({
                        ...answers,
                        [pet.id]: {
                          ...(answers[pet.id] ?? {}),
                          [q.slug]: { answer: 0 },
                        },
                      })
                    }
                    className={`flex-1 rounded-md border px-3 py-2 text-sm ${
                      current?.answer === 0
                        ? 'border-destructive bg-destructive/10 text-destructive'
                        : 'border-border'
                    }`}
                  >
                    {q.isAcknowledgement ? 'I do not confirm' : 'No'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ))}
      {feedingSection}
      <NavRow
        onBack={onBack}
        onNext={onNext}
        nextDisabled={!requiredAnswered || feedingIssues.length > 0}
      />
    </div>
  );
}

function ReviewStep({
  quote,
  selectedService,
  selectedPets,
  bookingSummary,
  vaccineGate,
  submitError,
  discountCode,
  onDiscountCodeChange,
  onRetryQuote,
  onConfirm,
  confirmLabel,
  signedIn,
  confirming,
  onBack,
}: {
  quote: QuoteSnapshot | null;
  selectedService: ServiceLite | null;
  selectedPets: PetLite[];
  bookingSummary: string | null;
  vaccineGate: VaccineRequiredError | null;
  submitError: string | null;
  discountCode: string;
  onDiscountCodeChange: (code: string) => void;
  onRetryQuote: () => void;
  onConfirm: () => void;
  confirmLabel: string;
  signedIn: boolean;
  confirming: boolean;
  onBack: () => void;
}) {
  if (vaccineGate) {
    return (
      <div className="space-y-4">
        <div className="rounded-md border border-destructive bg-destructive/10 p-4">
          <h3 className="text-sm font-medium text-destructive">Vaccination required</h3>
          <p className="mt-2 text-sm">
            We need an up-to-date vaccination record for {vaccineGate.petName ?? 'your pet'} before
            this booking can be made. Please upload a record from your pet&apos;s profile, then
            return here.
          </p>
        </div>
        <NavRow onBack={onBack} onNext={onRetryQuote} nextLabel="Re-check" />
      </div>
    );
  }

  if (!quote) {
    if (submitError) {
      return (
        <div className="space-y-4">
          <p className="text-sm text-destructive">{submitError}</p>
          <NavRow onBack={onBack} onNext={onRetryQuote} nextLabel="Retry" />
        </div>
      );
    }
    return <Skeleton lines={3} />;
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Here&apos;s a summary of your booking and total.
      </p>
      {/* Booking summary */}
      <div className="rounded-md border border-border bg-surface p-3 text-sm">
        <div className="font-medium">{selectedService?.name ?? 'Booking'}</div>
        {bookingSummary && <div className="num text-muted-foreground">{bookingSummary}</div>}
        {selectedPets.length > 0 && (
          <div className="text-muted-foreground">{selectedPets.map((p) => p.name).join(', ')}</div>
        )}
      </div>
      <div className="rounded-md border border-border p-4">
        {quote.quotes.map((q, i) => (
          <div key={`${q.petId}-${i}`} className="border-b border-border py-2 last:border-b-0">
            <div className="flex justify-between text-sm">
              <span>Base</span>
              <span className="num">{formatMoney(q.quote.base_minor, quote.currency)}</span>
            </div>
            {q.quote.modifiers.map((m, j) => (
              <div
                key={`${m.label}-${j}`}
                className="flex justify-between text-xs text-muted-foreground"
              >
                <span>{m.label}</span>
                <span className="num">{formatMoney(m.amount_minor, quote.currency)}</span>
              </div>
            ))}
            <div className="flex justify-between pt-1 text-sm font-medium">
              <span>Subtotal</span>
              <span className="num">{formatMoney(q.totalMinor, quote.currency)}</span>
            </div>
          </div>
        ))}
        <div className="mt-3 flex justify-between text-lg font-medium">
          <span>Total</span>
          <span className="num">{formatMoney(quote.totalMinor, quote.currency)}</span>
        </div>
        {selectedService?.paymentPolicy === 'deposit_then_balance' && (
          <p className="mt-2 text-xs text-muted-foreground">
            A deposit is collected now; the balance is auto-charged on check-in.
          </p>
        )}
      </div>
      {/* Discount code — applied at checkout, after sign-in. */}
      <div className="space-y-1">
        <label htmlFor="book-discount-code" className="text-xs font-medium text-muted-foreground">
          Discount code <span className="font-normal">(optional)</span>
        </label>
        <div className="flex gap-2">
          <input
            id="book-discount-code"
            type="text"
            value={discountCode}
            onChange={(e) => onDiscountCodeChange(e.target.value.toUpperCase())}
            placeholder="WELCOME10"
            className="flex-1 rounded-md border border-border bg-surface px-3 py-2 text-sm uppercase tracking-wider placeholder:normal-case placeholder:tracking-normal"
          />
        </div>
        <p className="text-[11px] text-muted-foreground">Applied at checkout.</p>
      </div>

      {!signedIn && (
        <p className="text-xs text-muted-foreground">
          This quote is saved. Sign in with a one-time email link on the next step to confirm — no
          password to remember.
        </p>
      )}

      {submitError && <p className="text-sm text-destructive">{submitError}</p>}
      <NavRow
        onBack={onBack}
        onNext={onConfirm}
        nextLabel={confirming ? 'Confirming…' : confirmLabel}
        nextDisabled={confirming}
      />
    </div>
  );
}

/**
 * Inline magic-link sign-in shown at the `account` step for visitors with no
 * session. The link's callback returns to this wizard URL (with the draft
 * id), so the flow resumes exactly here once verified.
 */
function SignInStep({ callbackURL, onBack }: { callbackURL: string; onBack: () => void }) {
  const [sentTo, setSentTo] = useState<string | null>(null);

  if (sentTo) {
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-3 rounded-md border border-border bg-surface p-4">
          <Mail size={18} strokeWidth={1.75} className="mt-0.5 shrink-0 text-primary" />
          <div className="text-sm">
            <p className="font-medium">Check your email</p>
            <p className="mt-1 text-muted-foreground">
              We sent a sign-in link to <span className="font-medium">{sentTo}</span>. Open it on
              this device and you&apos;ll come straight back here with your booking details saved.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setSentTo(null)}
          className="text-sm text-primary hover:underline"
        >
          Use a different email
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Almost there. Enter your email and we&apos;ll send a one-time link to confirm your booking —
        your details are saved.
      </p>
      <MagicLinkForm
        callbackURL={callbackURL}
        onSent={(email) => setSentTo(email)}
        submitLabel="Email me a sign-in link"
        inputId="book-sign-in-email"
      />
      <NavRow onBack={onBack} />
    </div>
  );
}

/**
 * Post-sign-in reconciliation of inline pets against pets already on file.
 * Each inline pet defaults to "add as new"; if the account already has pets,
 * the customer can match instead so we don't create duplicates.
 */
function MatchPetsStep({
  guestPets,
  accountPets,
  submitting,
  error,
  onConfirm,
  onBack,
}: {
  guestPets: GuestPet[];
  accountPets: PetLite[] | null;
  submitting: boolean;
  error: string | null;
  onConfirm: (mapping: Record<string, string | null>) => void;
  onBack: () => void;
}) {
  const [mapping, setMapping] = useState<Record<string, string | null>>(() => {
    const m: Record<string, string | null> = {};
    for (const p of guestPets) m[p.key] = null;
    return m;
  });

  // Pre-match by name (case-insensitive) once the account pets arrive.
  useEffect(() => {
    if (!accountPets || accountPets.length === 0) return;
    setMapping((cur) => {
      const next = { ...cur };
      for (const p of guestPets) {
        if (next[p.key]) continue;
        const hit = accountPets.find(
          (ap) =>
            ap.name.trim().toLowerCase() === p.name.trim().toLowerCase() &&
            ap.species === p.species,
        );
        if (hit) next[p.key] = hit.id;
      }
      return next;
    });
  }, [accountPets, guestPets]);

  if (accountPets === null) return <Skeleton lines={3} />;

  const usedIds = new Set(Object.values(mapping).filter((v): v is string => !!v));
  const duplicateMatch = usedIds.size !== Object.values(mapping).filter(Boolean).length;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {accountPets.length > 0
          ? 'You already have pets on file. Match each pet below, or add them as new.'
          : "We'll add these pets to your account and create your booking."}
      </p>
      <div className="space-y-2">
        {guestPets.map((p) => (
          <div
            key={p.key}
            className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border p-3"
          >
            <div className="text-sm">
              <span className="font-medium">{p.name}</span>{' '}
              <span className="text-muted-foreground">
                {p.species}
                {p.sizeCategory ? ` · ${p.sizeCategory.replace('_', ' ')}` : ''}
              </span>
            </div>
            {accountPets.length > 0 ? (
              <select
                aria-label={`Match ${p.name}`}
                value={mapping[p.key] ?? ''}
                onChange={(e) => setMapping({ ...mapping, [p.key]: e.target.value || null })}
                className="rounded-md border border-border bg-surface px-3 py-2 text-sm"
              >
                <option value="">Add as a new pet</option>
                {accountPets.map((ap) => (
                  <option key={ap.id} value={ap.id}>
                    {ap.name} ({ap.species})
                  </option>
                ))}
              </select>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                <Plus size={12} strokeWidth={1.75} />
                New pet
              </span>
            )}
          </div>
        ))}
      </div>
      {duplicateMatch && (
        <p className="text-sm text-destructive">Each pet on file can only be matched once.</p>
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}
      <NavRow
        onBack={onBack}
        onNext={() => onConfirm(mapping)}
        nextLabel={submitting ? 'Confirming…' : 'Confirm booking'}
        nextDisabled={submitting || duplicateMatch}
      />
    </div>
  );
}

function PaymentStep({
  booking,
  discountCode,
  onPaid,
}: {
  booking: CreateBookingResponse;
  discountCode?: string;
  onPaid: () => void;
}) {
  const publishableKey = getStripePublishableKey() ?? '';
  if (!publishableKey) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-destructive">
          Payments aren&apos;t configured for this kennel. Please contact them to confirm your
          booking.
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Finalising your booking — please complete payment below.
      </p>
      <PayBookingPanel
        bookingId={booking.primaryBookingId}
        publishableKey={publishableKey}
        onPaid={onPaid}
        {...(discountCode ? { discountCode } : {})}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

function NavRow({
  onBack,
  onNext,
  nextLabel,
  nextDisabled,
}: {
  onBack: () => void;
  onNext?: (() => void) | undefined;
  nextLabel?: string;
  nextDisabled?: boolean;
}) {
  return (
    <div className="mt-6 flex gap-3">
      <button
        type="button"
        className="rounded-md border border-border px-4 py-2 text-sm"
        onClick={onBack}
      >
        Back
      </button>
      {onNext && (
        <button
          type="button"
          className="rounded-md border border-border bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-50"
          disabled={nextDisabled}
          onClick={onNext}
        >
          {nextLabel ?? 'Next'}
        </button>
      )}
    </div>
  );
}

function Skeleton({ lines = 3 }: { lines?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: lines }).map((_, i) => (
        <div key={`sk-${i}`} className="h-8 animate-pulse rounded bg-muted/50" />
      ))}
    </div>
  );
}

function policyLabel(p: ServiceLite['paymentPolicy']): string {
  switch (p) {
    case 'pay_now':
      return 'Pay now';
    case 'pay_later':
      return 'Pay at the kennel';
    case 'deposit_then_balance':
      return 'Deposit + balance on check-in';
    case 'no_payment':
      return 'No payment required';
  }
}

// ---------------------------------------------------------------------------
// Confirmation page
// ---------------------------------------------------------------------------

export function BookConfirmation() {
  const { bookingId } = useParams<{ bookingId: string }>();
  // The booking detail API returns { booking: { id, type, status, totalMinor, ... }, type, stay/visit/appointment }
  // We only need the `booking` sub-object for the confirmation display.
  const [loaded, setLoaded] = useState<{
    id: string;
    type: string;
    status: string;
    totalMinor: number;
  } | null>(null);
  useEffect(() => {
    if (!bookingId) return;
    customerApi<{ booking: { id: string; type: string; status: string; totalMinor: number } }>(
      `/api/customer/bookings/${bookingId}`,
    )
      .then((r) => setLoaded(r.booking))
      .catch(() => setLoaded(null));
  }, [bookingId]);

  return (
    <BrandedShell>
      <div className="mx-auto max-w-2xl px-4 py-6 sm:px-6 sm:py-10">
        <div className="rounded-lg border border-border bg-card p-8 text-center">
          {loaded?.status === 'requested' ? (
            <>
              {/* Migration 0095 — request-and-approve mode: explain what happens next */}
              <h1 className="text-3xl font-medium">Request received</h1>
              <p className="mt-4 text-muted-foreground" data-testid="book-confirmation-requested">
                Thank you. This booking needs a quick review by the team before it&apos;s confirmed.
                Your space is held while they decide and nothing is charged yet &mdash; you&apos;ll
                get an email as soon as it&apos;s approved (with a link to pay if anything is due)
                or if they can&apos;t accommodate it.
              </p>
            </>
          ) : (
            <>
              <h1 className="text-3xl font-medium">Booking received</h1>
              <p className="mt-4 text-muted-foreground">
                Thank you. You&apos;ll get an email confirmation shortly, and the kennel will be in
                touch if they need any extra information.
              </p>
            </>
          )}
          {loaded && (
            <p className="mt-2 text-sm text-muted-foreground">
              Reference: <span className="num font-mono text-xs">{loaded.id}</span>
            </p>
          )}
        </div>
        <div className="mt-6 flex justify-center gap-3">
          <Link
            to="/account/bookings"
            className="rounded-md border border-border px-4 py-2 text-sm hover:border-primary"
          >
            View my bookings
          </Link>
          <Link
            to="/account"
            className="rounded-md border border-border bg-primary px-4 py-2 text-sm text-primary-foreground"
          >
            Back to my account
          </Link>
        </div>
      </div>
    </BrandedShell>
  );
}
